import type { Schemas } from '@npc/api-client'
import { useState } from 'react'

import { Button } from '../../components/button'
import { Card } from '../../components/card'
import { cn } from '../../components/cn'
import { StatusDot } from '../../components/tag'
import { t } from '../../copy'
import { T } from '../../copy/text'

/**
 * 右边那条实时探测流，以及底下那张绿色的「我猜这台是」。
 *
 * 每一行都是**人话在上、技术细节在下**：
 * 「连上了」在上面，`Ubuntu 22.04 · 4 vCPU · 8 GB` 在下面那行小字里。
 * 这两句话都由后端按 `docs/copy/onboarding.yaml` 的模板渲染好，前端不拼句子。
 */
export function ProbePanel({
  address,
  events,
  failure,
  hideDuplicateMessage,
  suggestion,
  ready,
  confirming,
  onAccept,
}: {
  address: string
  events: Schemas['OnboardingEvent'][]
  /**
   * 探测流彻底断了的时候那句话（SSE / 传输失败）。
   *
   * **工作流失败那句不走这里。** 那句已经在口令输入区旁说了一次，
   * 再刷到探测卡底部就是同一句话出现两次。
   *
   * **有它就得说。** 这一屏的验收标准是「贴一次 SSH，40 秒出档案」，
   * 而流断掉时原来的表现是**停在最后一行上一直转**——
   * 用户分不出「还在跑」和「已经废了」，只能一直等。
   */
  failure: string | null
  /**
   * 左侧已经说过的那句工作流失败。时间线保留 ×，但不重复同一句。
   */
  hideDuplicateMessage?: string | null
  suggestion: Schemas['HostSuggestion']
  /** 探测已经不会再长新行。认不出也要给一张卡，主按钮仍进详情。 */
  ready?: boolean
  confirming: boolean
  onAccept: (name?: string) => void
}) {
  return (
    <Card padding="none" className="overflow-hidden rounded-npc-card-lg">
      <div className="flex items-center gap-npc-gap-sm border-b border-npc-border-soft px-npc-card py-[15px]">
        {/* 断了就别再转了——一个还在跳的点是在说「还在跑」 */}
        <StatusDot tone={failure ? 'warn' : 'ok'} pulsing={!failure} />
        <span className="text-npc-body font-semibold">
          <T id="onboarding.probe.heading" />
        </span>
        <span className="ml-auto font-npc-mono text-npc-meta text-npc-ink-softer">{address}</span>
      </div>

      <div className="py-npc-gap-xs">
        {events.map((event) => (
          <ProbeRow key={event.step} event={event} hideDuplicateMessage={hideDuplicateMessage} />
        ))}
      </div>

      {failure ? (
        <p
          className="m-0 border-t border-npc-border-soft bg-npc-amber-bg px-npc-card py-npc-card-sm text-npc-body-sm text-npc-amber-ink"
          data-testid="onboard-probe-transport-failure"
        >
          {failure}
        </p>
      ) : null}

      {suggestion?.name ? (
        <Guess suggestion={suggestion} confirming={confirming} onAccept={onAccept} />
      ) : ready ? (
        <UnknownGuess confirming={confirming} onAccept={() => onAccept()} />
      ) : null}
    </Card>
  )
}

function ProbeRow({
  event,
  hideDuplicateMessage,
}: {
  event: Schemas['OnboardingEvent']
  hideDuplicateMessage?: string | null
}) {
  const done = event.state === 'ok'
  const failed = event.state === 'failed'
  const detail = typeof event.detail?.text === 'string' ? event.detail.text : undefined
  const hideMessage =
    failed && !!hideDuplicateMessage && event.message === hideDuplicateMessage

  return (
    <div
      className="flex items-start gap-npc-gap px-npc-card py-npc-row-y"
      data-testid="onboard-probe-row"
      data-state={event.state}
    >
      {/* ✓ 与 → 是字符，不是图标库。设计稿就是这么做的（CLAUDE.md §4.2） */}
      <span
        className={cn(
          'mt-[1px] flex size-[18px] shrink-0 items-center justify-center rounded-npc-full text-npc-group-label',
          done && 'bg-npc-green-bg text-npc-green',
          failed && 'bg-npc-red-bg text-npc-red',
          !done && !failed && 'animate-npc-pulse bg-npc-surface text-npc-ink-soft',
        )}
      >
        {done ? '✓' : failed ? '×' : '→'}
      </span>
      <div className="min-w-0">
        {hideMessage ? null : (
          <div
            className={cn(
              'text-npc-body-sm leading-[1.5]',
              done ? 'text-npc-ink' : failed ? 'text-npc-red-ink' : 'text-npc-ink-soft',
            )}
          >
            {event.message}
          </div>
        )}
        {detail ? (
          <div className="mt-[3px] font-npc-mono text-npc-meta text-npc-ink-softer">{detail}</div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * 「我猜这台是 ——」。
 *
 * 这是产品原则第 1 条「先做，再说」的界面形态：不是让用户填一张表，
 * 而是**先猜好，让他点头或者改名**。两个按钮都不是「取消」——
 * 机器已经接进来了，这一步只决定它叫什么、归谁。
 *
 * 「我自己起个名」必须真能改称呼：点开名字框，confirm 带上 `name`。
 * 留一个点了没下文的按钮，比没有这个按钮更糟。
 */
function Guess({
  suggestion,
  confirming,
  onAccept,
}: {
  suggestion: NonNullable<Schemas['HostSuggestion']>
  confirming: boolean
  onAccept: (name?: string) => void
}) {
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(suggestion.name ?? '')

  return (
    <div className="mx-npc-card mb-npc-card mt-[4px] rounded-npc-card-sm border border-npc-green-border bg-npc-green-bg p-[15px]">
      <div className="mb-npc-gap-sm text-npc-caption text-npc-green-deep">
        <T id="onboarding.guess.heading" />
      </div>
      <div className="mb-[4px] text-npc-section font-semibold text-npc-ink">{suggestion.name}</div>
      <div className="mb-npc-grid-sm text-npc-caption leading-[1.6] text-npc-green-deep">
        {suggestion.reason}
      </div>
      {renaming ? (
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label={t('onboarding.guess.rename')}
          className="mb-npc-grid-sm block w-full rounded-npc-card-sm border border-npc-green-border bg-npc-panel px-npc-grid-sm py-npc-gap-sm text-npc-body-sm text-npc-ink outline-none"
        />
      ) : null}
      <div className="flex gap-npc-gap-sm">
        <Button
          variant="primary"
          size="sm"
          disabled={confirming}
          onClick={() => onAccept(renaming ? name.trim() || suggestion.name : suggestion.name)}
        >
          <T id="onboarding.guess.confirm" />
        </Button>
        {renaming ? null : (
          <Button
            size="sm"
            className="border-npc-green-border bg-transparent text-npc-green-deep hover:border-npc-green"
            onClick={() => setRenaming(true)}
          >
            <T id="onboarding.guess.rename" />
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * 认不出。主按钮仍进详情，不阻塞接入。
 */
function UnknownGuess({
  confirming,
  onAccept,
}: {
  confirming: boolean
  onAccept: () => void
}) {
  return (
    <div
      className="mx-npc-card mb-npc-card mt-[4px] rounded-npc-card-sm border border-npc-border-soft bg-npc-surface p-[15px]"
      data-testid="onboard-guess-unknown"
    >
      <div className="mb-npc-grid-sm text-npc-caption leading-[1.6] text-npc-ink-soft">
        <T id="onboarding.guess.unknown" />
      </div>
      <Button variant="primary" size="sm" disabled={confirming} onClick={onAccept}>
        <T id="onboarding.guess.confirm" />
      </Button>
    </div>
  )
}
